import React, { useCallback, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import DeleteEventConfirmDialog from "./DeleteEventConfirmDialog";
import { formatEventTime, formatEventDate } from "../utils";

/** Panel when portaled: anchor handles placement (not `absolute` in class string). */
const menuItemsTransition =
  "z-[100] flex min-w-[160px] flex-col rounded-tl-lg rounded-bl-lg rounded-br-lg bg-white p-2 shadow-xl ring-1 ring-black/5 outline-none transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in";

function EventActionsMenu({ event, isMobile, onEdit, onRequestDelete }) {
  return (
    <Menu
      as="div"
      className={isMobile ? "relative" : "relative inline-block text-left"}
      onClick={isMobile ? (e) => e.stopPropagation() : undefined}
    >
      <MenuButton
        type="button"
        className={
          isMobile
            ? "flex h-10 w-10 items-center justify-center rounded-[10px] !bg-transparent text-white outline-none ring-0 focus:outline-none p-1"
            : "flex h-10 w-10 items-center justify-center rounded-lg !bg-[#2a0751] text-white outline-none ring-0 focus:outline-none p-1"
        }
      >
        <span className="sr-only">Open event actions</span>
        <EllipsisVerticalIcon className="h-5 w-5" />
      </MenuButton>
      <MenuItems
        portal
        anchor={{ to: "bottom end", gap: "6px", padding: "12px" }}
        transition
        className={menuItemsTransition}
      >
        <MenuItem>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded p-2 text-left text-sm text-black outline-none !bg-transparent atkinson-font"
            onClick={() => onEdit(event)}
          >
            <PencilSquareIcon
              className={isMobile ? "h-5 w-5 shrink-0" : "h-4 w-4 shrink-0"}
            />
            Edit Event
          </button>
        </MenuItem>
        <MenuItem>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded p-2 text-left text-sm text-red-600 outline-none !bg-transparent atkinson-font"
            onClick={() => onRequestDelete(event)}
          >
            <TrashIcon
              className={isMobile ? "h-5 w-5 shrink-0" : "h-4 w-4 shrink-0"}
            />
            Delete Event
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}

const EventTable = ({ events, onStreamEvent, onDelete, onEdit }) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const openDeleteConfirm = useCallback((event) => {
    setDeleteTarget({ id: event.id, name: event.name ?? "" });
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    if (!deleteBusy) setDeleteTarget(null);
  }, [deleteBusy]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await Promise.resolve(onDelete(deleteTarget.id));
    } finally {
      setDeleteBusy(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, onDelete]);

  return (
    <div className="rounded-[20px]">
      {/* Horizontal scroll only wraps the table so portaled menus are not clipped */}
      <div className="overflow-x-auto">
        {/* Desktop Table View */}
        <table className="hidden md:table w-full border-collapse min-w-[600px] atkinson-font">
          <thead>
            <tr>
              <th className="bg-[#d9d9d9] text-black font-bold p-3 text-left text-[20px]">
                Event
              </th>
              <th className="bg-[#d9d9d9] text-black font-bold p-3 text-left text-[20px]">
                Date/Time
              </th>
              <th className="bg-[#d9d9d9] text-black font-bold p-3 text-left text-[20px]">
                Status
              </th>
              <th
                colSpan="2"
                className="bg-[#d9d9d9] text-black font-bold p-3 text-left text-[20px]"
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {events.map((event, index) => {
              const dateToUse = event.scheduledToStartAt || event.createdDate;

              const isPast = event.isEnded;
              const statusLabel = isPast ? "Past" : "Restartable";
              const dateStr = `${formatEventDate(dateToUse)} ${formatEventTime(dateToUse)}`;

              const isEvenRow = index % 2 === 0;
              return (
                <tr
                  key={event.id}
                  className={isEvenRow ? "bg-[#d2d2d2]" : "bg-[#919191]"}
                >
                  <td className="p-4 text-black text-[20px] align-middle tracking-[-0.41px]">
                    {event.name}
                  </td>
                  <td className="p-4 text-black text-[20px] align-middle tracking-[-0.41px]">
                    {dateStr}
                  </td>
                  <td className="p-4 text-black text-[20px] align-middle tracking-[-0.41px]">
                    <span className={isPast ? "text-black" : "text-[#5701ae]"}>
                      {statusLabel}
                    </span>
                  </td>
                  <td className="p-4 text-black text-[20px] align-middle tracking-[-0.41px]">
                    {isPast ? (
                      <button
                        className="py-2 px-4 border-none rounded-lg cursor-not-allowed font-semibold text-[13px] whitespace-nowrap"
                        style={{
                          backgroundColor: "#9ca3af",
                          color: "#111827",
                          cursor: "not-allowed",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#9ca3af";
                        }}
                        disabled
                      >
                        Expired
                      </button>
                    ) : (
                      <button
                        className="py-2 px-4 border-none rounded-lg cursor-pointer font-semibold text-[13px] text-white transition-opacity duration-200 whitespace-nowrap bg-[#5701ae] hover:bg-[#7c4dff]"
                        onClick={() => {
                          if (
                            window.confirm(
                              "Are you sure you wish to start streaming this event now ?",
                            )
                          )
                            onStreamEvent(event.id);
                        }}
                      >
                        Start Live Captions
                      </button>
                    )}
                  </td>
                  <td className="p-4 text-black text-[20px] align-middle tracking-[-0.41px]">
                    <EventActionsMenu
                      event={event}
                      isMobile={false}
                      onEdit={onEdit}
                      onRequestDelete={openDeleteConfirm}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="flex flex-col gap-[15px] p-1 md:hidden">
        {events.map((event) => {
          const dateToUse = event.scheduledToStartAt || event.createdDate;
          const isPast = event.isEnded;
          const statusLabel = isPast ? "Past" : "Restartable";

          const dateStr = `${formatEventDate(dateToUse)} ${formatEventTime(dateToUse)}`;

          return (
            <div
              key={event.id}
              className={`border border-white rounded-[10px] p-2 flex items-center bg-[#17002e] transition-colors relative ${
                isPast
                  ? "cursor-not-allowed"
                  : "cursor-pointer hover:bg-white/10"
              }`}
              onClick={() => {
                if (!isPast) {
                  if (
                    window.confirm(
                      "Are you sure you wish to start streaming this event now ?",
                    )
                  )
                    onStreamEvent(event.id);
                }
              }}
            >
              <div className="min-w-0 flex-1 pr-3">
                <h3 className="m-0 truncate text-[1.25rem] font-bold text-white">
                  {event.name}
                </h3>
                <p className="-mt-1 m-0 truncate text-[1rem] text-[#e0e0e0] tracking-[-0.41px] atkinson-font">
                  {dateStr}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <span
                  className={`text-[12px] font-semibold whitespace-nowrap ${
                    isPast ? "text-gray-500" : "text-white"
                  }`}
                >
                  {statusLabel}
                </span>
                <EventActionsMenu
                  event={event}
                  isMobile
                  onEdit={onEdit}
                  onRequestDelete={openDeleteConfirm}
                />
              </div>
            </div>
          );
        })}
      </div>

      <DeleteEventConfirmDialog
        open={Boolean(deleteTarget)}
        eventName={deleteTarget?.name}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
        busy={deleteBusy}
      />
    </div>
  );
};

export default EventTable;
